import type { Id } from '@vp/shared-types';
import { layout, type Direction, type FlowModel, type GeoNode, type Measure, type NodeShape } from '@vp/dskhema';
import { fractionAt } from './explode';

/**
 * "Arrange as a scheme": shapes and the arrows between them drawn by hand are placed with the
 * DSkhema flowchart layout (docs/ru/DSKHEMA.md §6) in a chosen direction; the shapes keep their sizes.
 */
export interface ArrangeNode {
  id: Id;
  x: number;
  y: number;
  w: number;
  h: number;
  shape: NodeShape;
}

export interface ArrangeLink {
  id: Id;
  from: Id;
  to: Id;
  label: string;
}

export interface ArrangePlan {
  /** New top-left corner of every node. */
  moves: Map<Id, { x: number; y: number }>;
  /** Route of every link in world coordinates, where it leaves / meets its nodes (0..1 of their boxes) and its label spot. */
  links: { id: Id; points: number[]; fromAnchor: { x: number; y: number }; toAnchor: { x: number; y: number }; labelAt?: number }[];
}

/**
 * `scale`: world units per scheme unit (objects made at another zoom keep proportional gaps).
 * The first node without incoming links (in reading order) stays where it is.
 */
export function planArrange(nodes: ArrangeNode[], links: ArrangeLink[], direction: Direction, opts: { scale?: number; measure?: Measure; curved?: boolean } = {}): ArrangePlan {
  const s = opts.scale && opts.scale > 0 ? opts.scale : 1;
  const ids = new Set(nodes.map((n) => n.id));
  const used = links.filter((l) => l.from !== l.to && ids.has(l.from) && ids.has(l.to));
  // reading order across the flow keeps neighbours in their current order
  const vertical = direction === 'down' || direction === 'up';
  const ordered = [...nodes].sort((a, b) => (vertical ? a.x - b.x || a.y - b.y : a.y - b.y || a.x - b.x));
  const model: FlowModel = {
    kind: 'flow',
    direction,
    links: opts.curved ? 'curved' : 'orthogonal',
    nodes: ordered.map((n) => ({ id: n.id, label: '', shape: n.shape, style: {}, group: null, line: 0, size: { w: n.w / s, h: n.h / s } })),
    edges: used.map((l) => ({ from: l.from, to: l.to, kind: 'arrow', label: l.label, style: {}, line: 0 })),
    groups: [],
  };
  const g = opts.measure ? layout(model, opts.measure) : layout(model);
  const geo = new Map<string, GeoNode>();
  for (const it of g.items) if (it.k === 'node') geo.set(it.id, it);

  const incoming = new Set(used.map((l) => l.to));
  const root = ordered.find((n) => !incoming.has(n.id)) ?? ordered[0];
  const rg = geo.get(root.id);
  const ox = rg ? root.x - rg.x * s : root.x;
  const oy = rg ? root.y - rg.y * s : root.y;

  const moves = new Map<Id, { x: number; y: number }>();
  for (const n of nodes) {
    const p = geo.get(n.id);
    if (p) moves.set(n.id, { x: ox + p.x * s, y: oy + p.y * s });
  }

  // geometry links by their ends, in model order for links between the same pair
  const routes = new Map<string, number[][]>();
  const lbl = new Map<number[], { x: number; y: number }>();
  for (const it of g.items) {
    if (it.k !== 'edge' || !it.from || !it.to) continue;
    const key = it.from + '\u0001' + it.to;
    routes.set(key, [...(routes.get(key) ?? []), it.points]);
    if (it.label) lbl.set(it.points, { x: it.label.x + it.label.w / 2, y: it.label.y + it.label.h / 2 });
  }
  const anchor = (id: Id, px: number, py: number) => {
    const n = geo.get(id)!;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    return { x: clamp((px - n.x) / n.w), y: clamp((py - n.y) / n.h) };
  };
  const out: ArrangePlan['links'] = [];
  for (const l of used) {
    const pts = routes.get(l.from + '\u0001' + l.to)?.shift();
    if (!pts || pts.length < 4) continue;
    const c = lbl.get(pts);
    out.push({
      id: l.id,
      points: pts.map((v, i) => (i % 2 === 0 ? ox + v * s : oy + v * s)),
      fromAnchor: anchor(l.from, pts[0], pts[1]),
      toAnchor: anchor(l.to, pts[pts.length - 2], pts[pts.length - 1]),
      ...(c ? { labelAt: fractionAt(pts, c.x, c.y) } : {}),
    });
  }
  return { moves, links: out };
}
